'use client';

import { useMemo, useRef } from 'react';
import { StaticTreeDataProvider } from 'react-complex-tree';
import type {
    FileBrowserItems,
    FileBrowserItemData,
    FileBrowserUtils,
    FileBrowserTreeEnvironmentRefObject,
    FileBrowserTreeRefObject,
} from '@pyranger/types';
import {
    FILE_BROWSER_ROOT_INDEX,
    FILE_BROWSER_ROOT_ITEM,
} from '@pyranger/constants';

export const useFileBrowser = (): FileBrowserUtils => {
    // react-complex-tree expects the `items` object is a mutable object, so we need to memoize it to
    // refer to the same object. That's why we use `useMemo` and create it outside of the state
    // machine, so we can pass it in via its `input` object.
    const items = useMemo<FileBrowserItems>(
        () => ({
            [FILE_BROWSER_ROOT_INDEX]: FILE_BROWSER_ROOT_ITEM,
        }),
        [],
    );

    const dataProvider = useMemo(
        () =>
            new StaticTreeDataProvider<FileBrowserItemData>(
                items,
                (item, newName) => ({
                    ...item,
                    text: newName,
                }),
            ),
        [items],
    );

    const treeEnvironmentRef =
        useRef<FileBrowserTreeEnvironmentRefObject | null>(null);
    const treeRef = useRef<FileBrowserTreeRefObject | null>(null);

    return {
        dataProvider,
        items,
        treeEnvironmentRef,
        treeRef,
    };
};
